// Guarda o último link de acesso rápido (PIN) usado neste navegador.
// Assim, quando o colaborador clica em "Sair", ele volta para a tela do PIN
// do próprio link — e não para o login por e-mail/senha, ao qual não tem acesso.
export const STAFF_ACCESS_TOKEN_KEY = "futbux:staffAccessToken";
const STAFF_LOGOUT_RETURN_KEY = "futbux:staffLogoutReturn";
const STAFF_SESSION_SWITCH_KEY = "futbux:staffSessionSwitch";

export function saveStaffAccessToken(token: string) {
  try {
    localStorage.setItem(STAFF_ACCESS_TOKEN_KEY, token);
  } catch {
    /* storage bloqueado */
  }
}

export function getStaffAccessToken(): string | null {
  try {
    return localStorage.getItem(STAFF_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearStaffAccessToken() {
  try {
    localStorage.removeItem(STAFF_ACCESS_TOKEN_KEY);
  } catch {
    /* storage bloqueado */
  }
}

// Marca que a sessão atual deste navegador veio de um link de colaborador (PIN).
// Serve para manter os links independentes: o link geral (login) nunca deve
// continuar dentro da sessão aberta por um link de acesso rápido.
const STAFF_SESSION_MARK_KEY = "futbux:staffLinkSession";

export function markStaffLinkSession() {
  try {
    localStorage.setItem(STAFF_SESSION_MARK_KEY, "1");
  } catch {
    /* storage bloqueado */
  }
}

export function clearStaffLinkSession() {
  try {
    localStorage.removeItem(STAFF_SESSION_MARK_KEY);
  } catch {
    /* storage bloqueado */
  }
}

export function isStaffLinkSession(): boolean {
  try {
    return localStorage.getItem(STAFF_SESSION_MARK_KEY) === "1";
  } catch {
    return false;
  }
}

export function isStaffLogoutReturnPending(): boolean {
  try {
    return (
      sessionStorage.getItem(STAFF_LOGOUT_RETURN_KEY) === "1" ||
      sessionStorage.getItem(STAFF_SESSION_SWITCH_KEY) === "1"
    );
  } catch {
    return false;
  }
}

/** Evita que o listener global invalide a rota durante uma troca local de sessão. */
export function setStaffSessionSwitchPending(pending: boolean) {
  try {
    if (pending) sessionStorage.setItem(STAFF_SESSION_SWITCH_KEY, "1");
    else sessionStorage.removeItem(STAFF_SESSION_SWITCH_KEY);
  } catch {
    /* storage bloqueado */
  }
}

/** Consome a marca que diferencia o retorno do logout de uma abertura manual do link. */
export function consumeStaffLogoutReturn(): boolean {
  const pending = isStaffLogoutReturnPending();
  if (!pending) return false;
  try {
    sessionStorage.removeItem(STAFF_LOGOUT_RETURN_KEY);
  } catch {
    /* storage bloqueado */
  }
  return true;
}

/** Para onde mandar o usuário após sair: link de PIN, quando existir. */
export function signOutTarget(): string {
  const token = getStaffAccessToken();
  return token ? `/acesso/${token}` : "/auth";
}

/**
 * Saída única do app (botão do topo, do menu lateral e sessão expirada).
 * Colaborador que entrou por link volta para a tela do PIN do próprio link;
 * proprietário/usuário comum vai para o login por e-mail e senha.
 * Usa `location.replace` para o botão Voltar não restaurar uma tela protegida.
 */
export async function signOutAndRedirect(
  signOut: () => Promise<unknown>,
  opts: { forgetStaffLink?: boolean } = {},
) {
  const staffToken = opts.forgetStaffLink ? null : getStaffAccessToken();
  const target = staffToken ? `/acesso/${staffToken}` : "/auth";
  try {
    localStorage.removeItem("futgestao:activeStaffId");
    localStorage.removeItem(STAFF_SESSION_MARK_KEY);
    localStorage.removeItem("futgestao:activeStaffName");
    if (staffToken) {
      localStorage.setItem(STAFF_ACCESS_TOKEN_KEY, staffToken);
      sessionStorage.setItem(STAFF_LOGOUT_RETURN_KEY, "1");
    }
  } catch {
    /* storage bloqueado */
  }
  if (opts.forgetStaffLink) clearStaffAccessToken();
  try {
    await signOut();
  } catch {
    /* sessão já inválida: seguir para o destino mesmo assim */
  }
  // Alguns adaptadores de autenticação limpam o storage durante o SIGNED_OUT.
  // Regravar depois garante que ciclos consecutivos retornem ao mesmo link.
  if (staffToken) saveStaffAccessToken(staffToken);
  if (typeof window !== "undefined") window.location.replace(target);
}
